import { Alert, Button } from "react-bootstrap";
import { useFieldArray } from "react-hook-form";
import { FaPlus, FaTrash } from "react-icons/fa";
import { z } from "zod";

import ReactSelectFormGroup from "../../FormGroups/ReactSelectFormGroup.jsx";
import SelectFormGroup from "../../FormGroups/SelectFormGroup.jsx";

const PartsEnum = z.enum(["measurements", "results", "telemetry"]);

export const CloudConnectionSettingsSchema = z.object({
    cloud_connection: z
        .array(
            z.object({
                id: z.string().min(1, "Required"),
                parts: z
                    .array(PartsEnum)
                    .min(1, "Select at least 1 upload section"),
            }),
        )
        .optional(),
});

export const CloudConnectionSettingsValues = (settings) => ({
    cloud_connection: settings?.cloud_connection,
});

export default function CloudConnectionSettingsFormFields({
    control,
    errors,
    setValue,
    possibleConnections = [],
}) {
    const { fields, append, remove } = useFieldArray({
        control,
        name: "settings.cloud_connection",
    });

    const hasPossibleConnections = possibleConnections.length > 0;

    return (
        <>
            <h4>Cloud connections</h4>

            {!hasPossibleConnections && (
                <Alert variant="info" className="mb-3">
                    No cloud connections available for this organization.
                </Alert>
            )}

            {hasPossibleConnections && fields.length === 0 && (
                <Alert variant="secondary" className="mb-3">
                    No cloud connections configured.
                </Alert>
            )}

            {fields.map((field, index) => (
                <div
                    key={field.id}
                    className="d-flex justify-content-between align-items-start gap-2 mb-3"
                >
                    <div
                        className={`flex-grow-1 ${
                            index !== fields.length - 1
                                ? "border-bottom pb-3"
                                : ""
                        }`}
                    >
                        <SelectFormGroup
                            label="Cloud bucket"
                            name={`settings.cloud_connection.${index}.id`}
                            control={control}
                            errors={errors}
                            setValue={setValue}
                            options={possibleConnections}
                            disabledOption={{ id: "", name: "Select a bucket" }}
                        />

                        <ReactSelectFormGroup
                            label="Upload sections"
                            name={`settings.cloud_connection.${index}.parts`}
                            options={[
                                { id: "measurements", name: "Measurements" },
                                { id: "results", name: "Results" },
                                { id: "telemetry", name: "Telemetry" },
                            ]}
                            isMulti={true}
                            placeholder="Select upload sections"
                            control={control}
                            errors={errors}
                            setValue={setValue}
                        />
                    </div>

                    <Button
                        variant="danger"
                        type="button"
                        onClick={() => remove(index)}
                        className="flex-shrink-0 mt-4"
                    >
                        <FaTrash />
                    </Button>
                </div>
            ))}

            <div className="d-flex justify-content-end mt-2">
                <Button
                    variant="success"
                    type="button"
                    disabled={!hasPossibleConnections}
                    onClick={() =>
                        append({
                            id: "",
                            parts: [],
                        })
                    }
                >
                    <FaPlus className="me-2" />
                    Add connection
                </Button>
            </div>
        </>
    );
}
